import { ReactNode } from 'react';
import { Card } from '@/components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Skeleton } from '@/components/ui/skeleton';
import CopyButton from '@/components/ui/CopyButton';
import EmptyState from '@/components/ui/EmptyState';
import { cn } from '@/lib/utils';

interface AnalysisSectionProps {
  id?: string;
  title: string;
  icon?: ReactNode;
  copyText?: string;
  defaultOpen?: boolean;
  variant?: 'default' | 'highlight';
  /**
   * Deprecated — kept for backwards-compat with existing callers but no
   * longer affects layout. The card now always fills its column; prose
   * children that need a readable line length should wrap themselves in
   * `max-w-prose` (see ProjectSummary for the pattern). Remove this prop
   * once all callers stop passing it.
   */
  // eslint-disable-next-line react/no-unused-prop-types
  fullWidth?: boolean;
  isLoading?: boolean;
  hasData?: boolean;
  noDataText?: string;
  children: ReactNode;
}

function AnalysisSection({
  id,
  title,
  icon,
  copyText,
  defaultOpen = true,
  variant = 'default',
  isLoading = false,
  hasData = true,
  noDataText,
  children,
}: AnalysisSectionProps) {
  if (!hasData && !isLoading) {
    if (!noDataText) return null;
    return (
      <Card
        id={id}
        className={cn(
          'border-neutral-200/80 shadow-sm rounded-xl w-full scroll-mt-32',
          variant === 'highlight' && 'bg-primary/5 border-primary/15',
        )}
      >
        <div className="p-5">
          <div className="flex items-center gap-2 mb-3">
            {icon && <span>{icon}</span>}
            <h3 className="text-[15px] font-semibold text-neutral-900">{title}</h3>
          </div>
          <EmptyState description={noDataText} />
        </div>
      </Card>
    );
  }

  return (
    <Card
      id={id}
      className={cn(
        'border-neutral-200/80 shadow-sm rounded-xl w-full scroll-mt-32',
        variant === 'highlight' && 'bg-primary/5 border-primary/15',
      )}
    >
      <Accordion type="single" collapsible defaultValue={defaultOpen ? 'section' : undefined}>
        <AccordionItem value="section" className="border-b-0">
          <div className="flex items-center justify-between px-5 pt-1">
            <AccordionTrigger className="hover:no-underline flex-1 py-4 px-2 -mx-2 rounded-lg hover:bg-primary/5 transition-colors cursor-pointer">
              <div className="flex items-center gap-2">
                {icon && <span>{icon}</span>}
                <span className="text-[15px] font-semibold text-neutral-900">{title}</span>
              </div>
            </AccordionTrigger>
            {copyText && !isLoading && <CopyButton text={copyText} />}
          </div>
          <AccordionContent className="px-5 pb-5 pt-0">
            {isLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-4 w-full rounded-sm" />
                <Skeleton className="h-4 w-[85%] rounded-sm" />
                <Skeleton className="h-4 w-[70%] rounded-sm" />
              </div>
            ) : (
              <div>{children}</div>
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </Card>
  );
}

export default AnalysisSection;
